import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import FilmGrain from "@/components/FilmGrain";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import { Download, Trash2, Image as ImageIcon, Pencil, X, ImageOff, Eye, EyeOff, EyeOff as HideIcon, Eye as ShowIcon, Package, Bell, Send, Users, Sparkles, Loader2, Utensils } from "lucide-react";
import AdminTranslations from "@/pages/AdminTranslations";
import AdminPhotoLayout from "@/pages/AdminPhotoLayout";
import AdminVisits from "@/pages/AdminVisits";
import AdminSongs from "@/pages/AdminSongs";
import AdminGifts from "@/pages/AdminGifts";
import AdminChatbot from "@/pages/AdminChatbot";
import AdminPush from "@/pages/AdminPush";
import AdminLocationMap from "@/pages/AdminLocationMap";
import PwaManifest from "@/components/PwaManifest";
import AdminInstallChip from "@/components/AdminInstallChip";
import AdminInstallPromptModal from "@/components/AdminInstallPromptModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const toAbs = (url) => (url?.startsWith("http") ? url : `${BACKEND_URL}${url}`);

const AdminLogin = () => {
  const { adminLogin } = useAuth();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await adminLogin(pw);
    } catch (_e) {
      setErr("Incorrect admin password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="admin-login">
      <PwaManifest scope="admin" />
      <FilmGrain />
      <div className="px-5 sm:px-8 py-6 flex items-center justify-between">
        <span className="font-serif-display text-lg tracking-[0.04em]">Eli &amp; Mag</span>
        <Link to="/" className="editorial-kicker hover:text-foreground" data-testid="admin-home-link">Home</Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
          className="w-full max-w-[460px]"
        >
          <p className="editorial-kicker">For the couple</p>
          <h1 className="editorial-h1 mt-4 text-[40px] sm:text-[52px]">Admin</h1>
          <p className="mt-6 text-foreground/70 leading-relaxed">
            Sign in to review RSVPs and manage photos.
          </p>
          <form onSubmit={onSubmit} className="mt-10 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="adminpw" className="editorial-kicker">Admin password</Label>
              <div className="relative">
                <Input
                  id="adminpw"
                  type={showPassword ? "text" : "password"}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  autoFocus
                  data-testid="admin-login-input"
                  className="h-12 pr-12 border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent tracking-[0.18em]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  data-testid="admin-login-toggle-visibility"
                  className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-9 w-9 rounded-md text-foreground/45 hover:text-foreground hover:bg-foreground/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {err && <p className="text-sm text-destructive" data-testid="admin-login-error">{err}</p>}
            <Button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit"
              className="h-12 w-full rounded-xl bg-foreground text-background hover:bg-foreground/90 tracking-[0.22em] uppercase text-[12px]"
            >
              {busy ? "…" : "Sign in"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

const Admin = () => {
  const { isAdmin, adminLogout, adminToken } = useAuth();
  const navigate = useNavigate();
  const [rsvps, setRsvps] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [guestbook, setGuestbook] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [nudging, setNudging] = useState(false);
  const [savingSide, setSavingSide] = useState(null); // id of RSVP whose side is being saved
  const [loading, setLoading] = useState(true);
  // Photo lightbox
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  // RSVP edit dialog
  const [editingRsvp, setEditingRsvp] = useState(null);
  const [savingRsvp, setSavingRsvp] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p, g, br] = await Promise.all([
        api.get("/admin/rsvp"),
        api.get("/admin/photos"),
        api.get("/admin/guestbook").catch(() => ({ data: [] })),
        api.get("/admin/rsvp/breakdown").catch(() => ({ data: null })),
      ]);
      setRsvps(r.data || []);
      setPhotos(p.data || []);
      setGuestbook(g.data || []);
      setBreakdown(br.data || null);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        adminLogout();
      } else {
        toast.error("Failed to load admin data.");
      }
    } finally {
      setLoading(false);
    }
  }, [adminLogout]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const onExportCsv = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/rsvp.csv`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rsvp_eli_mag.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error("CSV export failed.");
    }
  }, [adminToken]);

  const [backingUp, setBackingUp] = useState(false);
  const onDownloadBackup = useCallback(async () => {
    if (backingUp) return;
    setBackingUp(true);
    const dismiss = toast.loading("Preparing backup — this can take a moment…");
    try {
      const res = await fetch(`${API_BASE}/admin/backup.zip`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error("backup failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "");
      a.download = `eli_mag_backup_${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded.", { id: dismiss });
    } catch (_e) {
      toast.error("Backup failed. Please try again.", { id: dismiss });
    } finally {
      setBackingUp(false);
    }
  }, [adminToken, backingUp]);

  const onDeletePhoto = useCallback(async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/admin/photos/${id}`);
      setPhotos((arr) => arr.filter((p) => p.id !== id));
      setLightboxPhoto((p) => (p && p.id === id ? null : p));
      toast.success("Photo deleted.");
    } catch (_e) {
      toast.error("Failed to delete photo.");
    }
  }, []);

  const onSaveRsvp = useCallback(async () => {
    if (!editingRsvp) return;
    setSavingRsvp(true);
    try {
      const { id, created_at, ...rest } = editingRsvp;
      // Coerce guest_count to integer just in case
      rest.guest_count = Number(rest.guest_count) || 1;
      const { data } = await api.patch(`/admin/rsvp/${id}`, rest);
      setRsvps((arr) => arr.map((r) => (r.id === id ? data : r)));
      setEditingRsvp(null);
      toast.success("RSVP updated.");
    } catch (_e) {
      toast.error("Failed to save RSVP.");
    } finally {
      setSavingRsvp(false);
    }
  }, [editingRsvp]);

  const onDeleteRsvp = useCallback(async (r) => {
    if (!window.confirm(`Delete RSVP from ${r.name}?`)) return;
    try {
      await api.delete(`/admin/rsvp/${r.id}`);
      setRsvps((arr) => arr.filter((x) => x.id !== r.id));
      toast.success("RSVP deleted.");
    } catch (_e) {
      toast.error("Failed to delete RSVP.");
    }
  }, []);

  const onSetSide = useCallback(async (rsvp, side) => {
    setSavingSide(rsvp.id);
    try {
      const value = side === "unset" ? null : side;
      const { data } = await api.patch(`/admin/rsvp/${rsvp.id}`, { side: value });
      setRsvps((arr) => arr.map((r) => (r.id === rsvp.id ? data : r)));
      // Refresh breakdown counts
      api.get("/admin/rsvp/breakdown").then((res) => setBreakdown(res.data || null)).catch(() => {});
    } catch (_e) {
      toast.error("Couldn't save side.");
    } finally {
      setSavingSide(null);
    }
  }, []);

  const onNudge = useCallback(async () => {
    if (!window.confirm("Send Andreas nudge push to ALL push subscribers?\n\nThose who already replied will just smile.")) return;
    setNudging(true);
    try {
      const { data } = await api.post("/admin/rsvp/nudge", { dry_run: false });
      const delivered = (data.results || []).filter((r) => r.ok).length;
      const failed = (data.results || []).filter((r) => !r.ok).length;
      toast.success(`Nudge fired · ${delivered} delivered · ${failed} failed`);
    } catch (_e) {
      toast.error("Nudge failed. Try again in a moment.");
    } finally {
      setNudging(false);
    }
  }, []);

  const onToggleHideGuestbook = useCallback(async (entry) => {
    try {
      const { data } = await api.patch(`/admin/guestbook/${entry.id}`, {
        hidden: !entry.hidden,
      });
      setGuestbook((arr) => arr.map((g) => (g.id === entry.id ? data : g)));
      toast.success(data.hidden ? "Entry hidden from public list." : "Entry made visible again.");
    } catch (_e) {
      toast.error("Failed to update entry.");
    }
  }, []);

  const onDeleteGuestbook = useCallback(async (entry) => {
    if (!window.confirm(`Permanently delete note from ${entry.name}?`)) return;
    try {
      await api.delete(`/admin/guestbook/${entry.id}`);
      setGuestbook((arr) => arr.filter((g) => g.id !== entry.id));
      toast.success("Entry deleted.");
    } catch (_e) {
      toast.error("Failed to delete entry.");
    }
  }, []);

  const stats = useMemo(() => {
    const total = rsvps.length;
    const attending = rsvps.filter((r) => r.attending === "yes").length;
    const guests = rsvps.filter((r) => r.attending === "yes").reduce((s, r) => s + (r.guest_count || 0), 0);
    return { total, attending, guests, photos: photos.length, guestbook: guestbook.length };
  }, [rsvps, photos, guestbook]);

  if (!isAdmin) return <AdminLogin />;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <PwaManifest scope="admin" />
      <AdminInstallPromptModal />
      <FilmGrain />
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur border-b border-border">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-serif-display text-lg tracking-[0.04em]">Eli &amp; Mag</span>
            <span className="editorial-kicker text-foreground/55">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <AdminInstallChip />
            <button
              type="button"
              onClick={() => navigate("/")}
              className="editorial-kicker text-foreground/55 hover:text-foreground"
              data-testid="admin-back-home"
            >
              Site
            </button>
            <button
              type="button"
              onClick={adminLogout}
              className="editorial-kicker text-foreground/55 hover:text-foreground"
              data-testid="admin-logout"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 py-10">
        <div className="grid sm:grid-cols-5 gap-4" data-testid="admin-stats">
          {[
            { k: "RSVPs", v: stats.total },
            { k: "Attending", v: stats.attending },
            { k: "Guests", v: stats.guests },
            { k: "Photos", v: stats.photos },
            { k: "Notes", v: stats.guestbook },
          ].map((c) => (
            <div key={c.k} className="surface-card p-5">
              <p className="editorial-kicker">{c.k}</p>
              <p className="font-serif-display text-4xl mt-2">{c.v}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="rsvp" className="mt-10">
          <TabsList className="bg-transparent border-b border-border w-full justify-start gap-4 sm:gap-6 h-auto p-0 flex-wrap">
            <TabsTrigger value="rsvp" data-testid="admin-tab-rsvp" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b data-[state=active]:border-accent rounded-none px-2 pb-3 text-[12px] tracking-[0.22em] uppercase text-foreground/45">RSVPs</TabsTrigger>
            <TabsTrigger value="photos" data-testid="admin-tab-photos" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b data-[state=active]:border-accent rounded-none px-2 pb-3 text-[12px] tracking-[0.22em] uppercase text-foreground/45">Photos</TabsTrigger>
            <TabsTrigger value="guestbook" data-testid="admin-tab-guestbook" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b data-[state=active]:border-accent rounded-none px-2 pb-3 text-[12px] tracking-[0.22em] uppercase text-foreground/45">Guestbook</TabsTrigger>
            <TabsTrigger value="content" data-testid="admin-tab-content" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b data-[state=active]:border-accent rounded-none px-2 pb-3 text-[12px] tracking-[0.22em] uppercase text-foreground/45">Content</TabsTrigger>
            <TabsTrigger value="layout" data-testid="admin-tab-layout" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b data-[state=active]:border-accent rounded-none px-2 pb-3 text-[12px] tracking-[0.22em] uppercase text-foreground/45">Layout</TabsTrigger>
            <TabsTrigger value="visits" data-testid="admin-tab-visits" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b data-[state=active]:border-accent rounded-none px-2 pb-3 text-[12px] tracking-[0.22em] uppercase text-foreground/45">Visits</TabsTrigger>
            <TabsTrigger value="songs" data-testid="admin-tab-songs" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b data-[state=active]:border-accent rounded-none px-2 pb-3 text-[12px] tracking-[0.22em] uppercase text-foreground/45">Songs</TabsTrigger>
            <TabsTrigger value="gifts" data-testid="admin-tab-gifts" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b data-[state=active]:border-accent rounded-none px-2 pb-3 text-[12px] tracking-[0.22em] uppercase text-foreground/45">Gifts</TabsTrigger>
            <TabsTrigger value="chatbot" data-testid="admin-tab-chatbot" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b data-[state=active]:border-accent rounded-none px-2 pb-3 text-[12px] tracking-[0.22em] uppercase text-foreground/45">Andreas</TabsTrigger>
            <TabsTrigger value="push" data-testid="admin-tab-push" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b data-[state=active]:border-accent rounded-none px-2 pb-3 text-[12px] tracking-[0.22em] uppercase text-foreground/45">Notifications</TabsTrigger>
            <TabsTrigger value="map" data-testid="admin-tab-map" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b data-[state=active]:border-accent rounded-none px-2 pb-3 text-[12px] tracking-[0.22em] uppercase text-foreground/45">Map</TabsTrigger>
          </TabsList>

          <TabsContent value="rsvp" className="mt-8">
            {/* Breakdown dashboard + Andreas nudge */}
            <div className="grid gap-4 md:grid-cols-3 mb-6" data-testid="admin-rsvp-dashboard">
              {/* Left: attendance + kids */}
              <div className="surface-card p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-accent" />
                  <p className="editorial-kicker">Attendance</p>
                </div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-foreground/60">Attending</span>
                    <span className="font-medium">{breakdown?.attending_heads ?? 0} <span className="text-foreground/50 text-xs">({breakdown?.attending_replies ?? 0} replies)</span></span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-foreground/60">Kids</span>
                    <span className="font-medium">{breakdown?.attending_kids ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-foreground/60">Declined</span>
                    <span className="font-medium text-foreground/60">{breakdown?.declined_replies ?? 0}</span>
                  </div>
                </div>
                {/* By side */}
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="editorial-kicker text-foreground/60 mb-2">By side (attending)</p>
                  <div className="space-y-1 text-xs">
                    {["elias", "mag", "both", "unset"].map((k) => (
                      <div key={k} className="flex items-center justify-between">
                        <span className="uppercase tracking-[0.14em] text-[10px] text-foreground/50">{k}</span>
                        <span className="tabular-nums text-foreground/80">{breakdown?.by_side?.[k] || 0}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Middle: by language */}
              <div className="surface-card p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-accent" />
                  <p className="editorial-kicker">By language</p>
                </div>
                <div className="space-y-1 text-sm">
                  {["en", "fr", "el", "pl"].map((lg) => (
                    <div key={lg} className="flex items-center justify-between">
                      <span className="uppercase tracking-[0.14em] text-[10px] text-foreground/50">{lg}</span>
                      <span className="tabular-nums text-foreground/80">{breakdown?.by_language?.[lg] || 0}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Utensils className="w-3.5 h-3.5 text-accent" />
                    <p className="editorial-kicker text-foreground/60">Meal quantities</p>
                  </div>
                  <div className="space-y-1 text-xs max-h-32 overflow-y-auto">
                    {breakdown?.by_meal && Object.keys(breakdown.by_meal).length > 0 ? (
                      Object.entries(breakdown.by_meal).map(([name, qty]) => (
                        <div key={name} className="flex items-center justify-between">
                          <span className="text-foreground/60 truncate mr-2">{name}</span>
                          <span className="tabular-nums text-foreground/80">× {qty}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-foreground/50 text-xs italic">No preorders yet</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: Andreas nudge */}
              <div className="surface-card p-5 flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <Bell className="w-4 h-4 text-accent" />
                  <p className="editorial-kicker">Andreas nudge</p>
                </div>
                <p className="text-sm text-foreground/70 leading-snug">
                  Draft &amp; send an LLM-written push in all four languages, nudging anyone who hasn&apos;t replied yet.
                </p>
                <p className="text-[11px] text-foreground/50 italic mt-2">
                  Delivered to every push subscriber. Those who already RSVPed just smile — Andreas keeps it light.
                </p>
                <div className="mt-auto pt-4">
                  <Button
                    onClick={onNudge}
                    disabled={nudging}
                    data-testid="admin-rsvp-nudge-button"
                    className="w-full h-10 rounded-lg bg-foreground text-background hover:bg-foreground/90 text-[11px] tracking-[0.18em] uppercase disabled:opacity-60"
                  >
                    {nudging ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Andreas is drafting…</>
                    ) : (
                      <><Send className="w-3.5 h-3.5 mr-2" /> Send Andreas nudge</>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <p className="editorial-kicker">{rsvps.length} submissions</p>
              <div className="flex items-center gap-2">
                <Button
                  onClick={onDownloadBackup}
                  disabled={backingUp}
                  data-testid="admin-backup-button"
                  variant="outline"
                  className="h-10 px-4 rounded-xl text-[12px] tracking-[0.18em] uppercase"
                  title="Download a .zip with RSVPs, guestbook, translations, photo layouts and photo files."
                >
                  <Package className="w-4 h-4 mr-2" />
                  {backingUp ? "Preparing…" : "Full backup"}
                </Button>
                <Button
                  onClick={onExportCsv}
                  data-testid="admin-export-csv-button"
                  className="h-10 px-4 rounded-xl bg-foreground text-background hover:bg-foreground/90 text-[12px] tracking-[0.18em] uppercase"
                >
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </div>
            </div>
            <div className="surface-card overflow-x-auto">
              <Table data-testid="admin-rsvp-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Att.</TableHead>
                    <TableHead>#</TableHead>
                    <TableHead>Arrival</TableHead>
                    <TableHead>Departure</TableHead>
                    <TableHead>Diet</TableHead>
                    <TableHead>Allergies</TableHead>
                    <TableHead>Transport</TableHead>
                    <TableHead>Song</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Menu order</TableHead>
                    <TableHead>Lang</TableHead>
                    <TableHead>Side</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={16} className="text-center editorial-kicker py-8">Loading…</TableCell></TableRow>
                  ) : rsvps.length === 0 ? (
                    <TableRow><TableCell colSpan={16} className="text-center editorial-kicker py-8">No replies yet.</TableCell></TableRow>
                  ) : (
                    rsvps.map((r) => {
                      const preorderText = Array.isArray(r.preorder) && r.preorder.length > 0
                        ? r.preorder
                            .map((p) => `${p.quantity}× ${p.name}${p.variant ? ` · ${p.variant}` : ""}`)
                            .join(", ")
                        : "";
                      return (
                      <TableRow key={r.id} data-testid={`admin-rsvp-row-${r.id}`}>
                        <TableCell className="whitespace-nowrap text-xs text-foreground/55">{new Date(r.created_at).toLocaleString()}</TableCell>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell className="text-foreground/75">{r.email}</TableCell>
                        <TableCell>{r.attending === "yes" ? "✓" : "—"}</TableCell>
                        <TableCell>{r.guest_count}</TableCell>
                        <TableCell className="whitespace-nowrap text-xs">{r.arrival_date || "—"}</TableCell>
                        <TableCell className="whitespace-nowrap text-xs">{r.departure_date || "—"}</TableCell>
                        <TableCell className="max-w-[160px] truncate">{r.dietary}</TableCell>
                        <TableCell className="max-w-[160px] truncate">{r.allergies}</TableCell>
                        <TableCell>{r.transportation}</TableCell>
                        <TableCell className="max-w-[160px] truncate">{r.song_request}</TableCell>
                        <TableCell className="max-w-[220px] truncate">{r.message}</TableCell>
                        <TableCell className="max-w-[260px] text-xs">
                          <span
                            title={preorderText}
                            data-testid={`admin-rsvp-preorder-${r.id}`}
                            className="line-clamp-3 whitespace-normal break-words"
                          >
                            {preorderText || "—"}
                          </span>
                        </TableCell>
                        <TableCell className="uppercase text-xs">{r.language}</TableCell>
                        <TableCell>
                          <select
                            value={r.side || "unset"}
                            onChange={(e) => onSetSide(r, e.target.value)}
                            disabled={savingSide === r.id}
                            data-testid={`admin-rsvp-side-${r.id}`}
                            className="h-8 text-xs bg-transparent border border-border rounded px-2 focus:outline-none focus:border-accent disabled:opacity-60"
                          >
                            <option value="unset">—</option>
                            <option value="elias">Elias</option>
                            <option value="mag">Mag</option>
                            <option value="both">Both</option>
                          </select>
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setEditingRsvp({ ...r })}
                              data-testid={`admin-rsvp-edit-${r.id}`}
                              className="inline-flex items-center h-8 w-8 justify-center rounded-md text-foreground/70 hover:text-foreground hover:bg-foreground/5 transition-colors"
                              aria-label="Edit"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => onDeleteRsvp(r)}
                              data-testid={`admin-rsvp-delete-${r.id}`}
                              className="inline-flex items-center h-8 w-8 justify-center rounded-md text-destructive/80 hover:text-destructive hover:bg-destructive/10 transition-colors"
                              aria-label="Delete"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="photos" className="mt-8">
            <p className="editorial-kicker mb-4">{photos.length} photos</p>
            {photos.length === 0 ? (
              <div className="surface-card p-10 text-center editorial-kicker">
                <ImageIcon className="mx-auto w-6 h-6 text-foreground/40 mb-3" />
                No photos uploaded yet.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-photos-grid">
                {photos.map((p) => (
                  <div key={p.id} className="surface-card overflow-hidden group relative">
                    <button
                      type="button"
                      onClick={() => setLightboxPhoto(p)}
                      data-testid={`admin-photo-open-${p.id}`}
                      className="block w-full aspect-square overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                      aria-label="Preview photo"
                    >
                      <img
                        src={toAbs(p.url)}
                        alt={p.original_name || "Guest photo"}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          e.currentTarget.parentElement.classList.add("bg-foreground/[0.03]", "flex", "items-center", "justify-center");
                          const placeholder = document.createElement("div");
                          placeholder.className = "flex flex-col items-center gap-2 text-foreground/40 editorial-kicker text-[10px]";
                          placeholder.innerHTML = '<span>Preview unavailable</span>';
                          e.currentTarget.parentElement.appendChild(placeholder);
                        }}
                      />
                    </button>
                    <div className="p-3">
                      <p className="text-[11px] text-foreground/55 truncate">{p.original_name || p.filename}</p>
                      <div className="mt-1 flex items-center justify-between gap-2">
                        <p className="text-[11px] text-foreground/45 truncate max-w-[55%]">
                          {p.uploader || "—"}
                        </p>
                        <button
                          type="button"
                          onClick={() => onDeletePhoto(p.id)}
                          data-testid={`admin-photo-delete-${p.id}`}
                          className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-destructive hover:text-destructive/80"
                          aria-label="Delete photo"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="content" className="mt-8">
            <AdminTranslations />
          </TabsContent>

          <TabsContent value="guestbook" className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <p className="editorial-kicker" data-testid="admin-guestbook-count">
                {guestbook.length} entries · {guestbook.filter((g) => g.hidden).length} hidden
              </p>
            </div>
            {loading ? (
              <div className="surface-card p-10 text-center editorial-kicker">Loading…</div>
            ) : guestbook.length === 0 ? (
              <div className="surface-card p-10 text-center editorial-kicker">
                No notes yet.
              </div>
            ) : (
              <ul className="space-y-4" data-testid="admin-guestbook-list">
                {guestbook.map((entry) => (
                  <li
                    key={entry.id}
                    className={`surface-card p-5 sm:p-6 ${entry.hidden ? "opacity-60" : ""}`}
                    data-testid={`admin-guestbook-entry-${entry.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="editorial-kicker text-accent">
                          {new Date(entry.created_at).toLocaleString()}
                          {entry.hidden && (
                            <span className="ml-3 text-destructive/80">HIDDEN</span>
                          )}
                        </p>
                        <p className="mt-3 font-serif-display italic text-xl leading-snug whitespace-pre-wrap">
                          {entry.message}
                        </p>
                        <p className="mt-3 text-[11px] tracking-[0.22em] uppercase text-foreground/60">
                          — {entry.name}
                          {entry.language && (
                            <span className="ml-2 text-foreground/40">
                              {entry.language.toUpperCase()}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="shrink-0 flex flex-col gap-2 items-end">
                        <button
                          type="button"
                          onClick={() => onToggleHideGuestbook(entry)}
                          data-testid={`admin-guestbook-toggle-${entry.id}`}
                          className="inline-flex items-center gap-2 h-9 px-3 rounded-md text-[11px] tracking-[0.18em] uppercase text-foreground/70 hover:text-foreground hover:bg-foreground/5 transition-colors"
                          aria-label={entry.hidden ? "Show entry" : "Hide entry"}
                        >
                          {entry.hidden ? (
                            <ShowIcon className="w-4 h-4" />
                          ) : (
                            <HideIcon className="w-4 h-4" />
                          )}
                          {entry.hidden ? "Show" : "Hide"}
                        </button>
                        <button
                          type="button"
                          onClick={() => onDeleteGuestbook(entry)}
                          data-testid={`admin-guestbook-delete-${entry.id}`}
                          className="inline-flex items-center gap-2 h-9 px-3 rounded-md text-[11px] tracking-[0.18em] uppercase text-destructive/80 hover:text-destructive hover:bg-destructive/10 transition-colors"
                          aria-label="Delete entry"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="layout" className="mt-8">
            <AdminPhotoLayout />
          </TabsContent>

          <TabsContent value="visits" className="mt-8">
            <AdminVisits />
          </TabsContent>

          <TabsContent value="songs" className="mt-8">
            <AdminSongs />
          </TabsContent>

          <TabsContent value="gifts" className="mt-8">
            <AdminGifts />
          </TabsContent>

          <TabsContent value="chatbot" className="mt-8">
            <AdminChatbot />
          </TabsContent>

          <TabsContent value="push" className="mt-8">
            <AdminPush />
          </TabsContent>

          <TabsContent value="map" className="mt-8">
            <AdminLocationMap />
          </TabsContent>
        </Tabs>
      </div>

      {/* Photo lightbox */}
      <Dialog open={!!lightboxPhoto} onOpenChange={(o) => !o && setLightboxPhoto(null)}>
        <DialogContent
          className="max-w-4xl bg-background border-border p-0 overflow-hidden"
          data-testid="admin-photo-lightbox"
        >
          {lightboxPhoto && (
            <div className="flex flex-col">
              <div className="relative bg-foreground/[0.03] flex items-center justify-center max-h-[75vh]">
                <img
                  src={toAbs(lightboxPhoto.url)}
                  alt={lightboxPhoto.original_name || "Photo"}
                  className="max-w-full max-h-[75vh] object-contain"
                  onError={(e) => {
                    e.currentTarget.replaceWith(
                      Object.assign(document.createElement("div"), {
                        className: "py-24 flex flex-col items-center gap-3 text-foreground/50",
                        innerHTML: '<div class="editorial-kicker">Preview unavailable</div><div class="text-xs">This photo file is no longer available on the storage.</div>',
                      })
                    );
                  }}
                />
              </div>
              <div className="p-5 border-t border-border flex flex-wrap gap-3 items-center justify-between">
                <div className="min-w-0">
                  <p className="font-medium truncate">{lightboxPhoto.original_name || lightboxPhoto.filename}</p>
                  <p className="text-xs text-foreground/55 mt-1">
                    Uploaded by <span className="font-medium">{lightboxPhoto.uploader || "anonymous"}</span>
                    {" · "}
                    {new Date(lightboxPhoto.uploaded_at).toLocaleString()}
                  </p>
                </div>
                <Button
                  variant="destructive"
                  onClick={() => onDeletePhoto(lightboxPhoto.id)}
                  data-testid="admin-photo-lightbox-delete"
                  className="h-10 px-4 text-[12px] tracking-[0.18em] uppercase"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete this photo
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* RSVP edit dialog */}
      <Dialog open={!!editingRsvp} onOpenChange={(o) => !o && setEditingRsvp(null)}>
        <DialogContent className="max-w-lg bg-background border-border" data-testid="admin-rsvp-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Edit RSVP</DialogTitle>
          </DialogHeader>
          {editingRsvp && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5 col-span-2">
                  <Label className="editorial-kicker">Name</Label>
                  <Input
                    value={editingRsvp.name || ""}
                    onChange={(e) => setEditingRsvp({ ...editingRsvp, name: e.target.value })}
                    data-testid="admin-rsvp-edit-name"
                  />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="editorial-kicker">Email</Label>
                  <Input
                    type="email"
                    value={editingRsvp.email || ""}
                    onChange={(e) => setEditingRsvp({ ...editingRsvp, email: e.target.value })}
                    data-testid="admin-rsvp-edit-email"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="editorial-kicker">Attending</Label>
                  <select
                    value={editingRsvp.attending || "yes"}
                    onChange={(e) => setEditingRsvp({ ...editingRsvp, attending: e.target.value })}
                    data-testid="admin-rsvp-edit-attending"
                    className="w-full h-10 px-3 border border-border rounded-md bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="editorial-kicker">Guest count</Label>
                  <Input
                    type="number"
                    min={0}
                    max={20}
                    value={editingRsvp.guest_count ?? 1}
                    onChange={(e) => setEditingRsvp({ ...editingRsvp, guest_count: Number(e.target.value) })}
                    data-testid="admin-rsvp-edit-guests"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="editorial-kicker">Arrival</Label>
                  <Input
                    type="date"
                    value={editingRsvp.arrival_date || ""}
                    onChange={(e) => setEditingRsvp({ ...editingRsvp, arrival_date: e.target.value })}
                    data-testid="admin-rsvp-edit-arrival"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="editorial-kicker">Departure</Label>
                  <Input
                    type="date"
                    value={editingRsvp.departure_date || ""}
                    onChange={(e) => setEditingRsvp({ ...editingRsvp, departure_date: e.target.value })}
                    data-testid="admin-rsvp-edit-departure"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="editorial-kicker">Diet</Label>
                  <Input
                    value={editingRsvp.dietary || ""}
                    onChange={(e) => setEditingRsvp({ ...editingRsvp, dietary: e.target.value })}
                    data-testid="admin-rsvp-edit-dietary"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="editorial-kicker">Transport</Label>
                  <Input
                    value={editingRsvp.transportation || ""}
                    onChange={(e) => setEditingRsvp({ ...editingRsvp, transportation: e.target.value })}
                    data-testid="admin-rsvp-edit-transport"
                  />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="editorial-kicker">Allergies</Label>
                  <Input
                    value={editingRsvp.allergies || ""}
                    onChange={(e) => setEditingRsvp({ ...editingRsvp, allergies: e.target.value })}
                    data-testid="admin-rsvp-edit-allergies"
                  />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="editorial-kicker">Song request</Label>
                  <Input
                    value={editingRsvp.song_request || ""}
                    onChange={(e) => setEditingRsvp({ ...editingRsvp, song_request: e.target.value })}
                    data-testid="admin-rsvp-edit-song"
                  />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="editorial-kicker">Message</Label>
                  <Textarea
                    rows={3}
                    value={editingRsvp.message || ""}
                    onChange={(e) => setEditingRsvp({ ...editingRsvp, message: e.target.value })}
                    data-testid="admin-rsvp-edit-message"
                  />
                </div>
                {Array.isArray(editingRsvp.preorder) && editingRsvp.preorder.length > 0 && (
                  <div className="space-y-1.5 col-span-2">
                    <Label className="editorial-kicker">Menu pre-order</Label>
                    <ul
                      className="text-sm text-foreground/80 leading-relaxed space-y-1"
                      data-testid="admin-rsvp-edit-preorder"
                    >
                      {editingRsvp.preorder.map((p, idx) => (
                        <li key={`${p.id}-${idx}`}>
                          <span className="font-medium">{p.quantity}×</span> {p.name}
                          {p.variant && <span className="text-foreground/60"> · {p.variant}</span>}
                        </li>
                      ))}
                    </ul>
                    <p className="editorial-kicker text-foreground/50">
                      Edit in the RSVP form on the site (this admin view is read-only for pre-orders).
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setEditingRsvp(null)}
              data-testid="admin-rsvp-edit-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={onSaveRsvp}
              disabled={savingRsvp}
              data-testid="admin-rsvp-edit-save"
              className="bg-foreground text-background hover:bg-foreground/90"
            >
              {savingRsvp ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
